/**
 * JALRAKSHAK System Technical Specifications & Subsystems
 */

export const SYSTEM_MODULES = [
  {
    id: 'sensors',
    title: 'Water Quality Sensor Array',
    badge: 'Multi-Parameter Telemetry',
    summary: 'Industrial-grade submerged probe array continuously measuring pH, Dissolved Oxygen (DO), Turbidity, Temperature, Nitrate, and Oxidation-Reduction Potential (ORP).',
    specs: [
      { name: 'Sampling Rate', value: '1 Hz (1 sample/second)' },
      { name: 'Probe Materials', value: 'Titanium housing & glass/optical electrodes' },
      { name: 'Calibration', value: 'Auto-zeroing & multi-point chemical calibration' },
      { name: 'Interface', value: 'RS-485 / Modbus RTU / I2C' }
    ],
    icon: 'activity',
    details: 'The sensor suite is integrated into an aerodynamic submerged keel probe that minimizes water drag while preventing bio-fouling through automated ultrasonic transducer pulses.'
  },
  {
    id: 'ai_edge',
    title: 'Edge AI Anomaly Engine',
    badge: 'Real-Time Neural Inference',
    summary: 'Onboard edge compute module running low-latency TensorFlow Lite / TinyML models for instantaneous pollution plume detection, outlier classification, and navigation route optimization.',
    specs: [
      { name: 'Compute Core', value: 'Edge AI Processor + Dual Microcontrollers' },
      { name: 'Model Latency', value: '< 45ms per inference frame' },
      { name: 'Detection Capabilities', value: 'Chemical spikes, oil slicks, floating macro-plastics' },
      { name: 'Fault Tolerance', value: 'Offline autonomous fallback logic' }
    ],
    icon: 'cpu',
    details: 'Processes incoming sensor readings and optical camera feeds in real time, detecting anomalies even when completely disconnected from cloud communications.'
  },
  {
    id: 'iot_cloud',
    title: 'IoT & Telemetry Bridge',
    badge: 'Dual-Band Connectivity',
    summary: 'Multi-layer wireless communication linking the vessel with the JALRAKSHAK Cloud Platform via 4G/LTE, long-range LoRaWAN, and MQTT secure telemetry brokers.',
    specs: [
      { name: 'Telemetry Protocol', value: 'MQTT over TLS 1.3 / HTTPS REST' },
      { name: 'Bandwidth Modes', value: 'LoRaWAN (15km range) + 4G LTE High-Res Stream' },
      { name: 'Cloud Ingestion', value: 'Time-series database with sub-second dashboard sync' },
      { name: 'Security', value: 'Hardware Cryptographic Element (AES-256)' }
    ],
    icon: 'wifi',
    details: 'Enables real-time remote monitoring by municipal bodies, environmental scientists, and automated notification dispatches via Webhook, SMS, and Email.'
  },
  {
    id: 'navigation',
    title: 'Autonomous Navigation System',
    badge: 'LiDAR & GPS Waypoint Pathing',
    summary: 'Intelligent dual-thruster differential steering guided by RTK-GPS centimeter-accurate positioning, solid-state LiDAR, and computer-vision obstacle avoidance.',
    specs: [
      { name: 'Positioning Accuracy', value: 'RTK GNSS (±2.5 cm precision)' },
      { name: 'Obstacle Avoidance', value: '360° LiDAR + Stereo Vision Depth Camera' },
      { name: 'Patrol Modes', value: 'Autonomous Lawn-mower Grid, Geofenced Patrol, Manual Override' },
      { name: 'Failsafe Protocol', value: 'Auto Return-to-Home on low battery or signal loss' }
    ],
    icon: 'compass',
    details: 'Allows the ASV to navigate complex water bodies, avoid boats, buoys, swimmers, and shallow banks safely without human intervention.'
  },
  {
    id: 'waste_collection',
    title: 'Hydrodynamic Waste Harvester',
    badge: 'Autonomous Surface Cleaning',
    summary: 'Continuous surface skimming conveyor belt with hydro-cyclonic intake and a 50 kg floating debris collection hopper.',
    specs: [
      { name: 'Payload Capacity', value: '50 kg (Macro-plastics, algae, organic debris)' },
      { name: 'Intake Width', value: '850 mm front hydro-sweep' },
      { name: 'Filtration Mesh', value: 'Dual-stage marine-grade stainless mesh (2mm)' },
      { name: 'Discharge Mechanism', value: 'Quick-release modular hopper bin for dock unloading' }
    ],
    icon: 'trash-2',
    details: 'Effectively traps single-use plastics, beverage bottles, aquatic weeds, and floating solid contaminants before they sink into benthic layers.'
  },
  {
    id: 'power',
    title: 'Solar-Assisted Power Architecture',
    badge: 'Zero-Emission Operations',
    summary: 'High-efficiency monocrystalline solar deck paired with smart LiFePO4 battery storage, delivering 14+ hours of continuous autonomous cleaning.',
    specs: [
      { name: 'Solar Deck Output', value: '380W High-Efficiency Monocrystalline' },
      { name: 'Battery Capacity', value: '48V 60Ah Lithium Iron Phosphate (LiFePO4)' },
      { name: 'Operational Endurance', value: '14+ Hours (Continuous daylight infinite float)' },
      { name: 'Carbon Footprint', value: 'Zero direct emissions • 100% clean electric' }
    ],
    icon: 'sun',
    details: 'Smart power management automatically adjusts thruster output and sensor polling intervals based on solar irradiation levels and mission urgency.'
  }
];
